import { useState, useEffect } from 'react';
import { X, ChevronLeft, Plus, MoveRight, Split, Receipt, Clock, Users, AlertCircle, Trash2 } from 'lucide-react';
import { posOrders } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';
import MoveTableModal from './MoveTableModal';
import SplitBillModal from './SplitBillModal';
import CancelItemsModal from './CancelItemsModal';

function elapsed(createdAt) {
  const diff = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  const m = Math.floor(diff / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

function fmtPrice(centavos) {
  return `₱${((centavos || 0) / 100).toFixed(2)}`;
}

const STATION_COLOR = {
  kitchen: 'bg-orange-100 text-orange-700',
  bar:     'bg-blue-100 text-blue-700',
  pastry:  'bg-pink-100 text-pink-700',
};

export default function OrderDetailPanel({
  table,
  order: initialOrder,
  tables,
  onClose,
  onAddItems,     // () → switch to product grid for this table
  onBillOut,      // (orderId) → bill out flow
  onOrderMoved,   // (newOrder) → update floor
  onOrderSplit,   // (result) → update floor
}) {
  const { posToken } = usePosStore();
  const [order, setOrder] = useState(initialOrder);
  const [showMove, setShowMove] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [billoutLoading, setBilloutLoading] = useState(false);
  const [cancelBilloutLoading, setCancelBilloutLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  // Refresh elapsed time every 30s
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const activeItems = order?.items?.filter(i => !i.voidedAt) || [];
  const voidedItems = order?.items?.filter(i => !!i.voidedAt) || [];
  const isSplit = order?.status === 'split';
  const isBilled = order?.status === 'billed';
  const isCancelled = order?.status === 'cancelled';

  const handleBillOut = async () => {
    setBilloutLoading(true);
    setError(null);
    try {
      const res = await posOrders.billOut(posToken, order._id);
      setOrder(res.data.order);
      onBillOut?.(res.data.order);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Bill out failed');
    } finally {
      setBilloutLoading(false);
    }
  };

  const handleCancelBillout = async () => {
    setCancelBilloutLoading(true);
    setError(null);
    try {
      const res = await posOrders.cancelBillout(posToken, order._id);
      setOrder(res.data.order);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to cancel bill out');
    } finally {
      setCancelBilloutLoading(false);
    }
  };

  const handleVoided = (updatedOrder) => {
    setOrder(updatedOrder);
    setShowCancel(false);
    onOrderMoved?.(updatedOrder);
  };

  const handleMoved = (newOrder) => {
    setOrder(newOrder);
    setShowMove(false);
    onOrderMoved?.(newOrder);
  };

  const handleSplit = (result) => {
    setShowSplit(false);
    onOrderSplit?.(result);
    onClose();
  };

  if (!order) return null;

  return (
    <>
      <div className="flex flex-col h-full bg-white border-l border-gray-200">
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 -ml-1">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="font-bold text-gray-900">{order.tableName}</span>
              {order.pax && (
                <span className="text-xs text-gray-500 flex items-center gap-0.5">
                  <Users className="h-3 w-3" /> {order.pax}
                </span>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 ml-7">
            <span className="capitalize">{order.type}</span>
            <span>·</span>
            <span className="flex items-center gap-0.5">
              <Clock className="h-3 w-3" /> {elapsed(order.createdAt)}
            </span>
            <span>·</span>
            <span>{order.waiterName}</span>
            {(isBilled || isSplit) && (
              <>
                <span>·</span>
                <span className={`font-semibold ${isBilled ? 'text-amber-600' : 'text-purple-600'}`}>
                  {order.status.toUpperCase()}
                </span>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="mx-3 mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start gap-1 flex-shrink-0">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-4 py-3 divide-y divide-gray-100">
          {activeItems.length === 0 && voidedItems.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No items</p>
          ) : (
            <>
              {activeItems.map((item) => (
                <div key={item._id} className="py-2.5 flex items-start gap-2">
                  <span className="text-sm font-semibold text-gray-500 w-5 text-right flex-shrink-0">
                    {item.quantity}×
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 leading-snug">{item.name}</p>
                    {item.notes && (
                      <p className="text-[11px] text-gray-400 mt-0.5 italic">{item.notes}</p>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium mt-0.5 inline-block ${STATION_COLOR[item.printStation] || 'bg-gray-100 text-gray-600'}`}>
                      {item.printStation}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 flex-shrink-0">
                    {fmtPrice(item.subtotal)}
                  </span>
                </div>
              ))}
              {voidedItems.map((item) => (
                <div key={item._id} className="py-2 flex items-start gap-2 opacity-50">
                  <span className="text-sm font-semibold text-gray-400 w-5 text-right flex-shrink-0 line-through">
                    {item.quantity}×
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-400 leading-snug line-through">{item.name}</p>
                    <span className="text-[10px] text-red-400">VOIDED · {item.voidReason}</span>
                  </div>
                  <span className="text-sm text-gray-400 flex-shrink-0 line-through">
                    {fmtPrice(item.subtotal)}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Totals */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 space-y-1">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span><span>{fmtPrice(order.subtotal)}</span>
          </div>
          {order.vatAmount > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>VAT (12%)</span><span>{fmtPrice(order.vatAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t border-gray-200">
            <span>Total</span><span>{fmtPrice(order.totalAmount)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 px-3 pb-3 grid grid-cols-2 gap-2">
          {!isBilled && !isSplit && !isCancelled && (
            <button
              onClick={onAddItems}
              className="flex items-center justify-center gap-1.5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 col-span-2"
            >
              <Plus className="h-4 w-4" /> Add Items
            </button>
          )}
          {!isBilled && !isSplit && !isCancelled && (
            <>
              <button
                onClick={() => setShowMove(true)}
                className="flex items-center justify-center gap-1.5 py-2 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg text-sm font-medium text-indigo-700"
              >
                <MoveRight className="h-4 w-4" /> Move Table
              </button>
              <button
                onClick={() => setShowSplit(true)}
                className="flex items-center justify-center gap-1.5 py-2 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg text-sm font-medium text-purple-700"
              >
                <Split className="h-4 w-4" /> Split Bill
              </button>
              <button
                onClick={() => setShowCancel(true)}
                disabled={activeItems.length === 0}
                className="flex items-center justify-center gap-1.5 py-2 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-medium text-red-700 disabled:opacity-40 col-span-2"
              >
                <Trash2 className="h-4 w-4" /> Cancel Items
              </button>
            </>
          )}
          {!isBilled && !isSplit && !isCancelled && (
            <button
              onClick={handleBillOut}
              disabled={billoutLoading || activeItems.length === 0}
              className="flex items-center justify-center gap-1.5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 col-span-2"
            >
              <Receipt className="h-4 w-4" />
              {billoutLoading ? 'Billing Out…' : 'Bill Out'}
            </button>
          )}
          {isBilled && (
            <>
              <button
                onClick={() => onBillOut?.(order)}
                className="col-span-2 flex items-center justify-center gap-1.5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold"
              >
                <Receipt className="h-4 w-4" /> Open Billing Statement
              </button>
              <button
                onClick={handleCancelBillout}
                disabled={cancelBilloutLoading}
                className="col-span-2 flex items-center justify-center gap-1.5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                {cancelBilloutLoading ? 'Cancelling…' : 'Cancel Bill Out'}
              </button>
            </>
          )}
          {isCancelled && (
            <div className="col-span-2 text-center py-2 text-sm text-red-600 font-medium bg-red-50 border border-red-200 rounded-lg">
              Order Cancelled
            </div>
          )}
        </div>
      </div>

      {showMove && (
        <MoveTableModal
          order={order}
          tables={tables}
          onClose={() => setShowMove(false)}
          onMoved={handleMoved}
        />
      )}

      {showSplit && (
        <SplitBillModal
          order={order}
          onClose={() => setShowSplit(false)}
          onSplit={handleSplit}
          requireApproval
        />
      )}

      {showCancel && (
        <CancelItemsModal
          order={order}
          onClose={() => setShowCancel(false)}
          onVoided={handleVoided}
        />
      )}
    </>
  );
}
